import { LineChart, Line, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getShopPageviews } from '@/requests/analytics'
import { useShop } from '@/context/shop-context'
import { useQuery } from '@tanstack/react-query'
import dayjs from "dayjs";

interface DataPoint {
  x: string;
  y: number;
}

interface AnalyticsData {
  pageviews: DataPoint[];
  sessions: DataPoint[];
}

interface WeeklyVisitData {
  name: string;
  Pageviews: number;
  Sessions: number;
}

export function WeeklyVisitOverview() {
  const processData = (apiResponse: AnalyticsData) => {
    if (!apiResponse) {
      return [];
    }
    const daysMap: Record<string, WeeklyVisitData>  = {};

    apiResponse.pageviews.forEach(({ x, y }) => {
      const day = dayjs(x).format('ddd');
      daysMap[x] = { name: day, Pageviews: y, Sessions: 0 };
    });

    apiResponse.sessions.forEach(({ x, y }) => {
      if (daysMap[x]) {
        daysMap[x].Sessions = y;
      }
    });
    
    return Object.values(daysMap);
  };

  const { activeShop } = useShop()
  const startAt = dayjs().subtract(7, "day").startOf("day").valueOf()
  const endAt = dayjs().endOf("day").valueOf()

  const { data, isLoading } = useQuery({
    queryKey: ['analytics.shop.weekly', activeShop?._id],
    queryFn: () => getShopPageviews(activeShop?._id || '', startAt, endAt, 'day'),
    enabled: !!activeShop?._id,
  })

  const chartData = processData(data as AnalyticsData)

  if (isLoading) {
    return <div className='h-[350px]' />
  }

  if (chartData.length === 0) {
    return (
      <>
        <div className='flex flex-col h-[350px] items-center justify-center'>
          <img src='/images/weekly-no-data.svg' alt='No analytics data' style={{ maxHeight: 180, marginBottom: 12 }} />
          <span style={{
            marginTop: 8,
            color: '#555',
            fontSize: 17,
            fontWeight: 500,
            textAlign: 'center',
            maxWidth: 320,
            lineHeight: 1.5
          }}>
            No data for this week yet. Insights will appear soon!
          </span>
        </div>
      </>
    )
  }

  return (
    <ResponsiveContainer width="100%" height={350}>
      <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#ffffff' }} stroke="#888" />
        <Tooltip
          contentStyle={{ backgroundColor: '#111', color: '#fff', borderRadius: 8, fontSize: 12 }}
          itemStyle={{ color: '#fff' }}
        />
        <Line type="monotone" dataKey="Pageviews" stroke="#aaaaaa" strokeWidth={2} dot={{ r: 2, fill: '#aaaaaa' }} />
        <Line type="monotone" dataKey="Sessions" stroke="#888888" strokeWidth={2} dot={{ r: 2, fill: '#888888' }} />
      </LineChart>
    </ResponsiveContainer>
  )
}
